"use client";

/**
 * BLOCO 5 — 3 blocos do Mockup_Site_Seraik_v2 trazidos pra LP atual (Ricardo):
 * (1) Ciclo em 3 passos (Conecta tudo / Cruza as fontes / Aponta a decisão),
 * (2) "Por que o Seraik" (6 cards), (3) O Squad (analistas + stats).
 * Paleta da marca (azul #14b4f9 → violeta #7b5cff → magenta #d20ffd), tema
 * escuro da LP. Copy limpa (sem IA/LLM). Reveals via LandingEffects (são <section>).
 */

import { useEffect } from "react";
import {
  Sparkles, ShieldCheck, Zap, Layers, Search, SlidersHorizontal,
} from "lucide-react";

const GRAD = "linear-gradient(100deg,#14b4f9,#7b5cff 50%,#d20ffd)";
const gradText = { background: GRAD, WebkitBackgroundClip: "text", backgroundClip: "text" as const, color: "transparent" };

const STEPS = [
  { n: "01", h: "Conecta tudo", p: "Mídia, GA4, Search Console, SEMrush, CRM, planilha e datalake. Consolidado e normalizado, sem planilha manual." },
  { n: "02", h: "Cruza as fontes", p: "O squad relaciona mídia, orgânico, CRM e vendas, e resolve dupla contagem antes de você ver o número." },
  { n: "03", h: "Aponta a decisão", p: "Um backlog de oportunidades priorizado por impacto e esforço, em reais. Da leitura à ação." },
];

const FEATURES = [
  { Icon: Sparkles, h: "Squad que nunca dorme", p: "Analistas por especialidade, mídia, SEO, CRM, atribuição, que respondem em qualquer tela com o contexto do seu negócio." },
  { Icon: ShieldCheck, h: "Confiança no número", p: "Cada card diz de onde vem o dado, o quão fresco está e o quanto pode confiar. Sem número fantasma, sem dupla contagem." },
  { Icon: Zap, h: "Da leitura à ação", p: "Um backlog único de oportunidades priorizado por impacto e esforço, em reais. O Seraik não para no gráfico." },
  { Icon: Layers, h: "Todas as fontes, um lugar", p: "Mídia paga, GA4, Search Console, SEMrush, CRM, planilha e datalake, consolidados e normalizados automaticamente." },
  { Icon: Search, h: "SEO + GEO", p: "Além do Google: monitore sua presença nos novos buscadores que respondem direto e transforme conteúdo em pipeline." },
  { Icon: SlidersHorizontal, h: "Do seu jeito", p: "Cards por modelo de negócio, metas por domínio, regras de qualidade que você controla. Escala sem perder a régua." },
];

// Squad REAL do produto (FAQ hero: 12 especialistas + aunica como Squad Lead).
// SEO = Camila (nao Ana Paula — decisao travada do Ricardo).
const SQUAD = [
  { in: "MY", nm: "Mayra", rl: "Negócios", g: "linear-gradient(135deg,#14b4f9,#7b5cff)" },
  { in: "SO", nm: "Sofia", rl: "Dados & Tendências", g: "linear-gradient(135deg,#7b5cff,#d20ffd)" },
  { in: "JO", nm: "José", rl: "Performance Digital", g: "linear-gradient(135deg,#d20ffd,#14b4f9)" },
  { in: "LU", nm: "Lucas", rl: "MarTech", g: "linear-gradient(135deg,#14b4f9,#d20ffd)" },
  { in: "EL", nm: "Elena", rl: "Adobe Analytics", g: "linear-gradient(135deg,#7b5cff,#14b4f9)" },
  { in: "RA", nm: "Rafael", rl: "Programática", g: "linear-gradient(135deg,#14b4f9,#7b5cff)" },
  { in: "BE", nm: "Beatriz", rl: "CRO", g: "linear-gradient(135deg,#7b5cff,#d20ffd)" },
  { in: "CA", nm: "Camila", rl: "SEO · GEO · Conteúdo", g: "linear-gradient(135deg,#d20ffd,#14b4f9)" },
  { in: "FE", nm: "Fernanda", rl: "CRM", g: "linear-gradient(135deg,#14b4f9,#d20ffd)" },
  { in: "AN", nm: "André", rl: "Analytics Engineering", g: "linear-gradient(135deg,#7b5cff,#14b4f9)" },
  { in: "MR", nm: "Maria", rl: "Cientista de Dados", g: "linear-gradient(135deg,#14b4f9,#7b5cff)" },
  { in: "PE", nm: "Pedro", rl: "Customer Success", g: "linear-gradient(135deg,#7b5cff,#d20ffd)" },
  { in: "IG", nm: "Igor", rl: "Atribuição", g: "linear-gradient(135deg,#d20ffd,#14b4f9)" },
];

const STATS = [
  { n: "100+", l: "cards de análise prontos" },
  { n: "24/7", l: "squad ao vivo" },
  { n: "1 clique", l: "da pergunta à resposta" },
  { n: "0", l: "planilhas manuais" },
];

export function LandingV2Blocks() {
  useEffect(() => {
    const steps = Array.from(document.querySelectorAll<HTMLElement>("[data-vstep]"));
    const panels = Array.from(document.querySelectorAll<HTMLElement>("[data-vpanel]"));
    if (!steps.length) return;
    const onScroll = () => {
      const mid = window.innerHeight * 0.5;
      let active = 0, best = Infinity;
      steps.forEach((el, i) => { const r = el.getBoundingClientRect(); const d = Math.abs(r.top + r.height / 2 - mid); if (d < best) { best = d; active = i; } });
      steps.forEach((el, i) => el.classList.toggle("vactive", i === active));
      panels.forEach((el, i) => el.classList.toggle("vactive", i === active));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      {/* (1) Ciclo em 3 passos — scrollytelling: painel troca conforme desce */}
      <section className="border-t border-border/40 py-24 relative overflow-hidden">
        <style>{`
          .vstep{opacity:.4;transition:opacity .4s;border-left:2px solid var(--border,#2a2a3a);padding-left:20px}
          .vstep.vactive{opacity:1;border-left-color:#7b5cff}
          .vpanel{position:absolute;inset:0;display:grid;place-items:center;opacity:0;transition:opacity .5s;text-align:center;padding:24px}
          .vpanel.vactive{opacity:1}
        `}</style>
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col gap-7">
              {STEPS.map((st, i) => (
                <div key={st.n} data-vstep={i} className={`vstep${i === 0 ? " vactive" : ""}`}>
                  <div className="text-[13px] font-extrabold tracking-widest" style={gradText}>{st.n}</div>
                  <h3 className="text-2xl md:text-3xl font-extrabold tracking-tight mt-1 text-foreground">{st.h}</h3>
                  <p className="text-muted-foreground mt-2 leading-relaxed max-w-md">{st.p}</p>
                </div>
              ))}
            </div>
            <div className="relative rounded-2xl border border-border bg-gradient-to-br from-[#14b4f9]/10 via-transparent to-[#d20ffd]/10 h-[360px] overflow-hidden">
              <div data-vpanel={0} className="vpanel vactive">
                <div><div className="text-6xl font-extrabold tracking-tight" style={gradText}>7</div><div className="text-muted-foreground mt-2">fontes conectadas</div></div>
              </div>
              <div data-vpanel={1} className="vpanel">
                <svg width="150" height="150" viewBox="0 0 42 42" aria-hidden="true"><defs><linearGradient id="ciclodg" x1="0" x2="1"><stop offset="0" stopColor="#14b4f9"/><stop offset="1" stopColor="#d20ffd"/></linearGradient></defs><circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--border,#2a2a3a)" strokeWidth="5"/><circle cx="21" cy="21" r="15.9" fill="none" stroke="url(#ciclodg)" strokeWidth="5" strokeDasharray="77 23" strokeDashoffset="25" strokeLinecap="round" transform="rotate(-90 21 21)"/><text x="21" y="23" textAnchor="middle" fontSize="7" fontWeight="800" fill="currentColor" className="text-foreground">77%</text></svg>
                <div className="text-muted-foreground mt-3 absolute bottom-8">dupla contagem resolvida</div>
              </div>
              <div data-vpanel={2} className="vpanel">
                <div><div className="text-5xl font-extrabold tracking-tight" style={gradText}>R$ 1,2M</div><div className="text-muted-foreground mt-2">oportunidade priorizada</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* (2) Por que o Seraik */}
      <section className="border-t border-border/40 py-24 bg-muted/10">
        <div className="container max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-[13px] font-bold tracking-widest uppercase" style={gradText}>Por que o Seraik</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mt-3 text-foreground">Não é mais um dashboard. É um time.</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-4">Dashboards mostram o número. O Seraik lê o número, cruza as fontes e te diz o que fazer, no seu idioma de negócio.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.h} className="rounded-2xl border border-border p-6 bg-card shadow-sm hover:-translate-y-1 hover:border-violet-500/40 hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-xl grid place-items-center mb-4" style={{ background: "linear-gradient(135deg,rgba(20,180,249,.18),rgba(210,15,253,.18))", border: "1px solid rgba(123,92,255,.25)" }}>
                  <f.Icon className="w-5 h-5 text-violet-600 dark:text-violet-200" />
                </div>
                <h3 className="text-lg font-bold">{f.h}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{f.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* (3) O Squad */}
      <section className="border-t border-border/40 py-24">
        <div className="container max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-[13px] font-bold tracking-widest uppercase" style={gradText}>O squad</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mt-3 text-foreground">Analistas com nome, não robôs genéricos</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-4">A <b className="text-foreground">aunica</b> é a Squad Lead: classifica a pergunta e aciona o especialista certo. Cada um responde onde você estiver, do painel ao chat. <span className="opacity-70">Diego (E-commerce) e Bruno (Inteligência de Mercado) chegando.</span></p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {SQUAD.map((a) => (
              <div key={a.nm} className="flex items-center gap-3 rounded-full border border-border bg-card pl-2 pr-4 py-2">
                <span className="w-9 h-9 rounded-full grid place-items-center text-white text-[13px] font-extrabold" style={{ background: a.g }}>{a.in}</span>
                <div><div className="text-sm font-bold leading-tight">{a.nm}</div><div className="text-[11px] text-muted-foreground">{a.rl}</div></div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {STATS.map((s) => (
              <div key={s.l} className="text-center rounded-2xl border border-border bg-card py-7">
                <div className="text-4xl font-extrabold tracking-tight" style={gradText}>{s.n}</div>
                <div className="text-muted-foreground text-sm mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
